"use client";

import { FetchCheckbox } from "@/fetch-ui/FetchCheckbox";
import { FetchConfirmationDialog } from "@/fetch-ui/FetchConfirmationDialog";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { AddRoomTypeMenu } from "@/features/event-details-rooms/_components/AddRoomTypeMenu";
import { ANCHORED_ROOM_TYPE } from "@/features/event-details/_logic/constants";
import { sumNightCounts } from "@/features/event-details-rooms/_logic/rooms";
import type { RoomType } from "@/lib/utils/accommodations";
import { parseRoomCount } from "@/lib/utils/room-count";
import { X } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { GUESTROOM_GRID_FIXED_COLUMNS } from "../../../_logic/constants";
import { applyQuickFill, computeSegmentedRoomNights, setNightRoomCount } from "../../../_logic/guestroom-nights";
import type {
  RoomTypeColumns,
  RoomTypeCounts,
  ShoulderNightActions,
  ShoulderSegment,
  StayNight,
} from "../../../_types/wizard-types";
import { AddShoulderNightRow } from "./AddShoulderNightRow";
import { GuestroomRow } from "./GuestroomRow";
import { GuestroomsSummary } from "./GuestroomsSummary";

interface GuestroomsGridProps {
  /** The grid's rows — pre-nights, peak nights, post-nights — from `useShoulderNights`. */
  stayNights: StayNight[];
  /** Current per-night, per-room-type breakdown (missing nights default to all-zero). */
  detailed: { [date: string]: RoomTypeCounts };
  /** Visible/addable room-type columns and their add/remove handlers. */
  columns: RoomTypeColumns;
  /** Pre/post-night add/remove controls. */
  shoulders: ShoulderNightActions;
  /** Expected headcount from the Headcount step — seeds the quick-add Single column so the common
   *  "one room per attendee" case is a single Apply click instead of a blank field. */
  numPeople: number;
  /** Called with the full updated breakdown on any edit. */
  onChange: (next: { [date: string]: RoomTypeCounts }) => void;
}

/**
 * Inline date × room-type grid — one row per stay night, one column per visible room type
 * (add/remove via `AddRoomTypeMenu`, matching the event-details Rooms section), a sticky "quick
 * add" row, a read-only nightly total, and a summary footer. Program (peak) nights are numbered
 * "Night 1…N"; the planner can add pre-nights above the first row and post-nights below the last
 * without touching the program dates. Quick add fills peak nights only unless the planner opts in
 * to pre/post nights.
 *
 * @param props - Rows, breakdown, column and pre/post controls, headcount, and change handler.
 * @returns The grid with its toolbar and footer.
 */
export function GuestroomsGrid({ stayNights, detailed, columns, shoulders, numPeople, onChange }: GuestroomsGridProps) {
  // Hooks
  // Read through a ref rather than closing over `detailed` directly, so the callback's identity
  // stays stable across edits — `detailed` changes every keystroke, and a callback that closed over
  // it would too, defeating `GuestroomRow`'s memoization on every row.
  const detailedRef = useRef(detailed);
  detailedRef.current = detailed;

  // States
  const [quickFill, setQuickFill] = useState<Partial<Record<RoomType, string>>>(() =>
    numPeople > 0 ? { [ANCHORED_ROOM_TYPE]: String(numPeople) } : {}
  );
  const [includeShouldersInQuickFill, setIncludeShouldersInQuickFill] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [pendingShoulderRemove, setPendingShoulderRemove] = useState<ShoulderSegment | null>(null);

  // Derived values
  const visibleColumns = columns.visible;
  const hasShoulders = stayNights.some((night) => night.segment !== "peak");
  const quickFillsShoulders = hasShoulders && includeShouldersInQuickFill;
  const quickFillTarget = quickFillsShoulders ? "every night" : "peak nights";
  const firstNight = stayNights[0];
  const lastNight = stayNights[stayNights.length - 1];
  const colSpan = visibleColumns.length + GUESTROOM_GRID_FIXED_COLUMNS;
  const hasAnyValue = useMemo(
    () => stayNights.some(({ date }) => visibleColumns.some((type) => (detailed[date]?.[type] ?? 0) > 0)),
    [stayNights, visibleColumns, detailed]
  );
  const summary = useMemo(() => computeSegmentedRoomNights(stayNights, detailed), [stayNights, detailed]);

  // Handlers
  const handleCellChange = useCallback(
    (night: StayNight, roomType: RoomType, raw: string) => {
      onChange(setNightRoomCount(detailedRef.current, night, roomType, parseRoomCount(raw)));
    },
    [onChange]
  );

  const handleQuickFillApply = () => {
    onChange(
      applyQuickFill(detailed, stayNights, {
        values: quickFill,
        columns: visibleColumns,
        includeShoulders: quickFillsShoulders,
      })
    );
  };

  // Removing a pre/post night that holds rooms drops those counts, so it's confirmed first like
  // Clear table; an empty one goes straight away.
  const { onRemove: removeShoulder } = shoulders;
  const handleShoulderRemove = useCallback(
    (segment: ShoulderSegment) => {
      const night = segment === "pre" ? firstNight : lastNight;
      if (night && sumNightCounts(detailedRef.current[night.date]) > 0) setPendingShoulderRemove(segment);
      else removeShoulder(segment);
    },
    [firstNight, lastNight, removeShoulder]
  );

  const handleConfirmShoulderRemove = () => {
    if (pendingShoulderRemove) removeShoulder(pendingShoulderRemove);
    setPendingShoulderRemove(null);
  };

  // Resets every night's counts across every column back to blank — a fast way to start the
  // breakdown over after a bad quick-add or a change of plans, without removing the room-type
  // columns themselves. Confirmed first since it can wipe a full week's worth of entries in one click.
  const handleClearTable = () => {
    onChange({});
    setShowClearConfirm(false);
  };

  // Render
  return (
    <div className="mt-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Room breakdown</span>
        <div className="flex items-center gap-1">
          {hasAnyValue && (
            <button
              type="button"
              onClick={() => setShowClearConfirm(true)}
              className="rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              Clear table
            </button>
          )}
          <AddRoomTypeMenu availableTypes={columns.availableForAdd} onSelect={columns.onAdd} />
          <FetchConfirmationDialog
            isOpen={showClearConfirm}
            onClose={() => setShowClearConfirm(false)}
            onConfirm={handleClearTable}
            title="Clear the room breakdown?"
            message="This removes every room count you've entered across all nights. This can't be undone."
            confirmText="Clear table"
            type="danger"
          />
          <FetchConfirmationDialog
            isOpen={pendingShoulderRemove !== null}
            onClose={() => setPendingShoulderRemove(null)}
            onConfirm={handleConfirmShoulderRemove}
            title={`Remove this ${pendingShoulderRemove === "post" ? "post-night" : "pre-night"}?`}
            message="This removes the room counts you've entered for that night. This can't be undone."
            confirmText="Remove night"
            type="danger"
          />
        </div>
      </div>
      <div className="max-h-96 overflow-auto rounded-xl border border-gray-100 bg-white scrollbar-visible">
        <table className="w-full min-w-[560px] border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-b border-gray-200">
              <th
                scope="col"
                className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                Night
              </th>
              {visibleColumns.map((type) => (
                <th
                  key={type}
                  scope="col"
                  className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
                >
                  <span className="relative inline-block">
                    {type}
                    {visibleColumns.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => columns.onRemove(type)}
                        aria-label={`Hide ${type} column`}
                        className="absolute left-full top-1/2 ml-1 -translate-y-1/2 rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 focus-visible:ring-offset-1"
                      >
                        <X className="size-3.5" aria-hidden />
                      </button>
                    ) : null}
                  </span>
                </th>
              ))}
              <th
                scope="col"
                className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                Total
              </th>
            </tr>
            <tr className="border-b border-gray-200 bg-indigo-50/40">
              <td className="px-5 py-2">
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-indigo-500 whitespace-nowrap">
                    Quick add — {quickFillTarget}
                  </span>
                  {hasShoulders && (
                    <FetchCheckbox
                      size="sm"
                      checked={includeShouldersInQuickFill}
                      onChange={setIncludeShouldersInQuickFill}
                      label="Include pre/post nights"
                    />
                  )}
                </div>
              </td>
              {visibleColumns.map((type) => (
                <td key={type} className="px-3 py-2 align-middle">
                  <div className="mx-auto w-16">
                    <FetchInput
                      type="number"
                      hideStepper
                      min={0}
                      placeholder="—"
                      value={quickFill[type] ?? ""}
                      onChange={(e) => setQuickFill((prev) => ({ ...prev, [type]: e.target.value }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      aria-label={`Quick-add ${type} rooms for ${quickFillTarget}`}
                      className="text-center tabular-nums"
                    />
                  </div>
                </td>
              ))}
              <td className="px-3 py-2 text-center">
                <button
                  type="button"
                  onClick={handleQuickFillApply}
                  aria-label={`Apply quick-add values to ${quickFillTarget}`}
                  className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-indigo-600 transition-colors hover:bg-indigo-100"
                >
                  Apply
                </button>
              </td>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {shoulders.canAdd.pre && <AddShoulderNightRow segment="pre" colSpan={colSpan} onAdd={shoulders.onAdd} />}
            {stayNights.map((night) => {
              const isOutermostShoulder = night.segment !== "peak" && (night === firstNight || night === lastNight);
              return (
                <GuestroomRow
                  key={night.date}
                  night={night}
                  counts={detailed[night.date]}
                  visibleColumns={visibleColumns}
                  onCellChange={handleCellChange}
                  onRemove={isOutermostShoulder ? handleShoulderRemove : undefined}
                />
              );
            })}
            {shoulders.canAdd.post && <AddShoulderNightRow segment="post" colSpan={colSpan} onAdd={shoulders.onAdd} />}
          </tbody>
        </table>
      </div>
      <GuestroomsSummary summary={summary} />
    </div>
  );
}
